import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  MoreItem,
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { CalendarTeamEventEditComponent } from "./CalendarTeamEventEditComponent";
import { CalendarTeamEventEditScreenQuery } from "./__generated__/CalendarTeamEventEditScreenQuery.graphql";
import { CalendarTeamEventEditScreenComponent_event$key } from "./__generated__/CalendarTeamEventEditScreenComponent_event.graphql";
import { useIntl } from "react-intl";

import { CalendarTeamEventEditScreenComponent_storeCalendars$key } from "./__generated__/CalendarTeamEventEditScreenComponent_storeCalendars.graphql";

type Props = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "CalendarTeamEventEditScreen">,
    UserLoggedInNavigatorNavigationProp<"CalendarTeamEventEdit">
  >;
  route: RouteProp<ParamList, "CalendarTeamEventEditScreen">;
};

type ParamList = {
  CalendarTeamEventEditScreen: {
    teamId: string;
    eventId: string;
    connections: string[];
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export function InnerCalendarTeamEventEditScreenComponent(props: {
  calendars: CalendarTeamEventEditScreenComponent_storeCalendars$key;
  event: CalendarTeamEventEditScreenComponent_event$key;
  onClose: () => void;
  timezone: string;
  connections: string[];
}) {
  const calendars = useFragment(
    graphql`
      fragment CalendarTeamEventEditScreenComponent_storeCalendars on StoreCalendar
      @relay(plural: true) {
        ...CalendarTeamEventEditComponent_storeCalendars
      }
    `,
    props.calendars
  );

  const event = useFragment(
    graphql`
      fragment CalendarTeamEventEditScreenComponent_event on CalendarEvent {
        id
        title
        startAt {
          isoString
        }
        endAt {
          isoString
        }
        isAllDayEvent
        storeCalendar {
          id
          isExternal
        }
      }
    `,
    props.event
  );

  const [dirty, setDirty] = useState(false);
  const [title, _setTitle] = useState(event.title ?? "");
  const [isAllDay, _setIsAllDay] = useState(event.isAllDayEvent ?? false);
  const [startAt, _setStartAt] = useState<DateTime>(
    DateTime.fromISO(event.startAt.isoString)
  );
  const [endAt, _setEndAt] = useState<DateTime>(
    DateTime.fromISO(event.endAt.isoString)
  );
  const [calendarId, _setCalendarId] = useState<string | null>(
    event.storeCalendar?.id
  );
  const setTitle = useCallback((title: string) => {
    _setTitle(title);
    setDirty(true);
  }, []);
  const setIsAllDay = useCallback((isAllDay: boolean) => {
    _setIsAllDay(isAllDay);
    setDirty(true);
  }, []);
  const setStartAt = useCallback((startAt: DateTime) => {
    _setStartAt(startAt);
    setDirty(true);
  }, []);
  const setEndAt = useCallback((endAt: DateTime) => {
    _setEndAt(endAt);
    setDirty(true);
  }, []);
  const setCalendarId = useCallback((calendarId: string) => {
    _setCalendarId(calendarId);
    setDirty(true);
  }, []);

  const [updateCalendarEvent, updateCalendarEventLoading] = useMutation(graphql`
    mutation CalendarTeamEventEditScreenUpdateCalendarEventMutation(
      $input: UpdateCalendarEventInput!
    ) {
      updateCalendarEvent(input: $input) {
        id
        ...WidgetCalendarOverviewEventItem_event
        ...CalendarTeamEventItem_event
      }
    }
  `);

  const [deleteCalendarEvent] = useMutation(graphql`
    mutation CalendarTeamEventEditScreenDeleteCalendarEventMutation(
      $eventId: ID!
      $connections: [ID!]!
    ) {
      deleteCalendarEvent(id: $eventId) {
        id @deleteEdge(connections: $connections)
      }
    }
  `);

  const disabled = useMemo(
    () => title.length === 0 || calendarId === null || startAt > endAt,
    [title.length, calendarId, startAt, endAt]
  );
  const intl = useIntl();
  const more: MoreItem[] = useMemo(
    () => [
      {
        label: intl.formatMessage({
          defaultMessage: "Delete Event",
          description: "CalendarTeamEventEditScreen delete button title",
        }),
        color: "negative",
        icon: "trash",
        type: "button",
        onPress: () => {
          deleteCalendarEvent({
            variables: {
              eventId: event.id,
              connections: props.connections,
            },
            onCompleted: () => {
              props.onClose();
            },
          });
        },
      },
    ],
    [deleteCalendarEvent, event.id, intl, props]
  );
  useNavigationHeader(
    event.storeCalendar?.isExternal || !dirty
      ? {
          type: "CLOSE",
          onClose: props.onClose,
          title: "",
          defaultContentStyle: true,
          more: event.storeCalendar?.isExternal ? undefined : more,
        }
      : {
          type: "SAVE",
          more,
          onCancel: props.onClose,
          onSave: () => {
            const startAtISO = startAt.toISO();
            const endAtISO = endAt.toISO();
            if (
              !startAtISO ||
              !endAtISO ||
              startAtISO > endAtISO ||
              !calendarId ||
              title.length === 0
            ) {
              return;
            }
            updateCalendarEvent({
              variables: {
                input: {
                  id: event.id,
                  title,
                  startAt: startAtISO,
                  endAt: endAtISO,
                  isAllDayEvent: isAllDay,
                  calendarId,
                },
              },
              onCompleted: () => {
                props.onClose();
              },
            });
          },
          saveLoading: updateCalendarEventLoading,
          disabled,
          title: "",
          defaultContentStyle: true,
        }
  );

  if (!calendarId) {
    return null;
  }

  return (
    <CalendarTeamEventEditComponent
      calendars={calendars}
      calendarId={calendarId}
      setCalendarId={setCalendarId}
      isAllDay={isAllDay}
      setIsAllDay={setIsAllDay}
      startAt={startAt}
      setStartAt={setStartAt}
      endAt={endAt}
      setEndAt={setEndAt}
      title={title}
      setTitle={setTitle}
      editable={!event.storeCalendar?.isExternal}
      timezone={props.timezone}
    />
  );
}

function CalendarTeamEventEditScreenComponent(
  props: Props & { fetchKey: string }
) {
  const data = useLazyLoadQuery<CalendarTeamEventEditScreenQuery>(
    graphql`
      query CalendarTeamEventEditScreenQuery($teamId: ID!, $eventId: ID!) {
        store {
          timezone
          calendarEvent(id: $eventId) {
            ...CalendarTeamEventEditScreenComponent_event
          }
          team(id: $teamId) {
            calendars(includeExternal: true) {
              ...CalendarTeamEventEditScreenComponent_storeCalendars
            }
          }
        }
      }
    `,
    { teamId: props.route.params.teamId, eventId: props.route.params.eventId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.store || !data.store.team || !data.store.calendarEvent) {
    return null;
  }

  return (
    <InnerCalendarTeamEventEditScreenComponent
      onClose={() => props.navigation.goBack()}
      calendars={data.store.team.calendars}
      event={data.store.calendarEvent}
      timezone={data.store.timezone}
      connections={props.route.params.connections}
    />
  );
}

function CalendarTeamEventEditScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <CalendarTeamEventEditScreenComponent {...props} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const CalendarTeamEventEdit = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CalendarTeamEventEdit">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator initialRouteName="CalendarTeamEventEditScreen">
        <Stack.Screen
          name="CalendarTeamEventEditScreen"
          component={CalendarTeamEventEditScreen}
          initialParams={{
            teamId: props.route.params.teamId,
            eventId: props.route.params.eventId,
            connections: props.route.params.connections,
          }}
          options={navigationHeaderOptions({
            type: "CLOSE",
            onClose: () => props.navigation.goBack(),
            title: "",
            defaultContentStyle: true,
          })}
        />
      </Stack.Navigator>
    );
  }
);
